import { Tooltip, useIsOverflowing } from '@/dc-shared-web/src'
import { cn } from '@dc-shared-web/lib'
import Link from 'next/link'
import { ReactNode } from 'react'

export type ListItemCardProps = {
  /** Optional description slot to display below the title */
  description?: ReactNode
  /** URL that the card links to when clicked */
  href: string
  /** Optional icon element to display on the left side of the card */
  icon?: ReactNode
  /** Optional flag to indicate if this is a new item */
  isNew?: boolean
  /** Optional element to display on the right side of the card */
  rightSlot?: ReactNode
  /** The main title text of the card */
  title: string
}

const getGridColumns = (hasIcon: boolean, hasRightSlot: boolean) => {
  if (hasIcon && hasRightSlot) return 'tw-grid-cols-[20px_1fr_auto]'
  if (hasIcon) return 'tw-grid-cols-[20px_1fr]'
  if (hasRightSlot) return 'tw-grid-cols-[1fr_auto]'
  return 'tw-grid-cols-1'
}

export const ListItemCard = ({
  description,
  href,
  icon,
  isNew,
  rightSlot,
  title,
  ...rest
}: ListItemCardProps) => {
  const { isOverflowing, textRef } = useIsOverflowing(title)

  return (
    <div {...rest} className='tw-relative'>
      <Link
        href={href}
        className={cn(
          'tw-block tw-bg-white hover:tw-bg-gray-100 tw-transition-colors tw-border-[1px] tw-border-[#e4e6ef] tw-rounded-[6px] tw-p-2.5 tw-pr-4',
          isNew && 'tw-animate-list-item-card-new'
        )}
      >
        <div
          className={cn(
            'tw-grid tw-gap-2 tw-items-center',
            getGridColumns(Boolean(icon), Boolean(rightSlot))
          )}
        >
          {icon && <div>{icon}</div>}
          <div className='tw-truncate'>
            <Tooltip content={title} active={isOverflowing} withArrow>
              <p ref={textRef} className='tw-truncate'>
                {title}
              </p>
            </Tooltip>
          </div>
        </div>
        {description}
      </Link>
      {rightSlot && (
        <div className='tw-flex tw-absolute tw-right-2 tw-top-2'>
          {rightSlot}
        </div>
      )}
    </div>
  )
}
